
import React, { createContext, useContext, ReactNode, useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { supabase } from '../lib/supabase';
import { 
  Patient, Plan, Appointment, Nutritionist, CashEntry, 
  AnthropometricAssessment, Reminder, PlanTemplate, 
  ConsultationSettings, MessageTemplate, AnnualGoal, Task, User,
  FeedbackSettings, FeedbackStatus, FeedbackPlan
} from '../types';
import { foodDatabase } from '../data/foodDatabase';

interface DataContextProps {
  // Auth
  users: User[];
  currentUser: User | null;
  login: (email: string, pass: string) => Promise<boolean>;
  logout: () => void;
  registerUser: (email: string, pass: string) => void;
  updateUser: (user: User) => Promise<void>;
  resetUserPassword: (userId: string) => void;
  
  // Data
  patients: Patient[];
  plans: Plan[];
  appointments: Appointment[];
  nutritionist: Nutritionist | null;
  cashEntries: CashEntry[];
  reminders: Reminder[];
  tasks: Task[];
  taskCategories: string[];
  planTemplates: PlanTemplate[];
  annualGoals: AnnualGoal[];
  consultationSettings: ConsultationSettings;
  messageTemplates: MessageTemplate[];
  feedbackSettings: FeedbackSettings;
  feedbackStatuses: FeedbackStatus[];
  
  // Actions
  setNutritionist: (n: Nutritionist | null) => void;
  addPatient: (patient: any) => void;
  updatePatient: (patient: Patient) => void;
  deletePatient: (patientId: number) => void;
  setPatients: React.Dispatch<React.SetStateAction<Patient[]>>;
  addPlan: (plan: Plan) => void;
  updatePlan: (plan: Plan) => void;
  deletePlan: (planId: number) => void;
  setPlans: React.Dispatch<React.SetStateAction<Plan[]>>;
  addAppointment: (appointment: Appointment) => void;
  updateAppointment: (appointment: Appointment) => void;
  deleteAppointment: (appointmentId: number) => void;
  setAppointments: React.Dispatch<React.SetStateAction<Appointment[]>>;
  addCashEntry: (entry: CashEntry) => void;
  deleteCashEntry: (entryId: number) => void;
  updateCashEntryByReference: (referenceId: number, data: Partial<CashEntry>) => void;
  setCashEntries: React.Dispatch<React.SetStateAction<CashEntry[]>>;
  addReminder: (reminder: Reminder) => void;
  updateReminder: (reminder: Reminder) => void;
  deleteReminder: (reminderId: string | number) => void;
  setReminders: React.Dispatch<React.SetStateAction<Reminder[]>>;
  addTask: (task: Omit<Task, 'id' | 'userId' | 'createdAt'>) => void;
  updateTask: (task: Task) => void;
  deleteTask: (taskId: number) => void;
  addTaskCategory: (category: string) => void;
  addAssessment: (patientId: number, assessment: Omit<AnthropometricAssessment, 'id'>) => void;
  updateAssessment: (patientId: number, assessment: AnthropometricAssessment) => void;
  deleteAssessment: (patientId: number, assessmentId: number) => void;
  setAnnualGoals: React.Dispatch<React.SetStateAction<AnnualGoal[]>>;
  setPlanTemplates: React.Dispatch<React.SetStateAction<PlanTemplate[]>>;
  setMessageTemplates: React.Dispatch<React.SetStateAction<MessageTemplate[]>>;
  setConsultationSettings: React.Dispatch<React.SetStateAction<ConsultationSettings>>;
  setFeedbackSettings: (settings: FeedbackSettings) => void;
  updateFeedbackStatus: (patientId: number, date: string) => void;
  
  // Global Import
  importBackup: (data: any) => void;

  // System
  foods: typeof foodDatabase;
  isSyncing: boolean;
  lastSync: Date | null;
  isCloudConfigured: boolean;
  syncError: string | null;
  syncAutoReminders: (newSettings?: ConsultationSettings) => void;
  forceSyncToSupabase: () => Promise<void>;
  
  // Toast Feedback
  toast: { message: string; type: 'success' | 'error' | 'sync' | null };
  setToast: (t: { message: string; type: 'success' | 'error' | 'sync' | null }) => void;
}

const DataContext = createContext<DataContextProps | undefined>(undefined);

const DEFAULT_TEMPLATES: MessageTemplate[] = [
  { id: 1, userId: 'all', name: 'lembrete de consulta', text: "Olá, [NOME_PACIENTE]! Não se esqueça da sua consulta com [NOME_NUTRI] no dia [DATA] às [HORA]. Por favor, confirme o agendamento." },
  { id: 2, userId: 'all', name: 'Lembrete de agendamento de retorno', text: "Olá, [NOME_PACIENTE]. Já fazem mais de [DIAS_POSCONSULTA] dias desde sua última consulta e já já é hora de retornar para conferir os resultados e fazer os ajustes necessários para dar continuidade ao seu acompanhamento. Vamos deixar agendada sua próxima consulta?" },
  { id: 3, userId: 'all', name: 'felicitações aniversário', text: "🎉 Parabéns, [NOME_PACIENTE]! 🎂 Que você continue firme no cuidado com sua saúde. Um grande abraço do Nutri [NOME_NUTRI] 💪" },
  { id: 4, userId: 'all', name: 'Lembrete de consulta (perto da consulta)', text: "Olá, [NOME_PACIENTE]! Sua consulta está chegando! Dia [DATA] às [HORA]. Local: [LOCAL]." },
  { id: 1760299179687, userId: 'all', name: 'solicitação de feedback da consulta', text: "Oi [NOME_PACIENTE], espero que esteja tudo bem! Já se passaram alguns dias desde nossa última consulta e eu gostaria de saber seu feedback :)" },
  { id: 1760545590972, userId: 'all', name: 'Dieta liberada', text: "Oi [NOME_PACIENTE]! 👋 Acabei de atualizar seu plano alimentar no nosso app. Por favor, baixe ou abra o aplicativo Webdiet no seu celular para acessar a sua nova dieta. \n\nDê uma olhada com calma e me conte o que achou! Estou aqui para fazer os ajustes que você precisar e para ajudar a personalizar tudo conforme sua rotina e preferências. \n\nFique a vontade, vamos alcançar seus objetivos juntos!" },
  { id: 999, userId: 'all', name: 'Instruções pré-consulta', text: "Olá, [NOME_PACIENTE]! Tudo bem? Sua consulta está chegando!\n\n📅 Consulta com [NOME_NUTRI]\n🗓️ Dia [DATA], [DIA_SEMANA], às [HORA]\n📍 [LOCAL]\n\nPara que possamos aproveitar ao máximo o nosso encontro, recomendo:\n\n👕 Venha com roupas leves, facilitando a avaliação física.\n⏰ Chegue com antecedência, evitando atrasos e garantindo que tenhamos tempo suficiente para conversar com tranquilidade sobre seus objetivos.\n\nSe tiver alguma dúvida ou precisar de mais informações, estou à disposição. Até breve!" }
];

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [users, setUsers] = useLocalStorage<User[]>('4nutri_users', [{ id: 'admin', email: 'admin', password: 'admin', role: 'admin' }]);
  const [currentUser, setCurrentUser] = useLocalStorage<User | null>('4nutri_current_user', null);

  const [allPatients, setAllPatients] = useLocalStorage<Patient[]>('4nutri_patients', []);
  const [allPlans, setAllPlans] = useLocalStorage<Plan[]>('4nutri_plans', []);
  const [allAppointments, setAllAppointments] = useLocalStorage<Appointment[]>('4nutri_appointments', []);
  const [allNutritionists, setAllNutritionists] = useLocalStorage<Record<string, Nutritionist>>('4nutri_nutritionists', {});
  const [allCashEntries, setAllCashEntries] = useLocalStorage<CashEntry[]>('4nutri_cashEntries', []);
  const [allReminders, setAllReminders] = useLocalStorage<Reminder[]>('4nutri_reminders', []);
  const [allTasks, setAllTasks] = useLocalStorage<Task[]>('4nutri_tasks', []);
  const [allTaskCategories, setAllTaskCategories] = useLocalStorage<Record<string, string[]>>('4nutri_task_categories', {});
  const [allPlanTemplates, setAllPlanTemplates] = useLocalStorage<PlanTemplate[]>('4nutri_plan_templates', []);
  const [allAnnualGoals, setAllAnnualGoals] = useLocalStorage<AnnualGoal[]>('4nutri_annual_goals', []);
  const [allMessageTemplates, setAllMessageTemplates] = useLocalStorage<MessageTemplate[]>('4nutri_msg_templates', DEFAULT_TEMPLATES);
  const [allSettings, setAllSettings] = useLocalStorage<Record<string, ConsultationSettings>>('4nutri_settings', {});
  const [allFeedbackSettings, setAllFeedbackSettings] = useLocalStorage<Record<string, FeedbackSettings>>('4nutri_feedback_settings', {});
  const [allFeedbackStatuses, setAllFeedbackStatuses] = useLocalStorage<FeedbackStatus[]>('4nutri_feedback_statuses', []);

  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'sync' | null }>({ message: '', type: null });
  
  const latestDataRef = useRef<any>({});
  
  useEffect(() => {
    latestDataRef.current = {
      users, allPatients, allPlans, allAppointments, allNutritionists,
      allCashEntries, allReminders, allTasks, allPlanTemplates,
      allAnnualGoals, allMessageTemplates, allSettings,
      allFeedbackSettings, allFeedbackStatuses, allTaskCategories
    };
  }, [users, allPatients, allPlans, allAppointments, allNutritionists, allCashEntries, allReminders, allTasks, allPlanTemplates, allAnnualGoals, allMessageTemplates, allSettings]);

  const forceSyncToSupabase = useCallback(async () => {
    if (!supabase) return;
    try {
      setIsSyncing(true);
      const dataToSync = latestDataRef.current;
      const payload = { id: 'multi_user_data', data: dataToSync, updated_at: new Date().toISOString() };
      const { error } = await supabase.from('user_data').upsert(payload, { onConflict: 'id' });
      if (error) throw error;
      setLastSync(new Date());
      setSyncError(null);
      setToast({ message: "Dados salvos na nuvem com sucesso", type: 'success' });
      setTimeout(() => setToast({ message: '', type: null }), 3000);
    } catch (err: any) { 
      setSyncError(err.message);
      if (err.message && (err.message.includes('row-level security') || err.code === '42501')) {
        setToast({ message: "Erro de Permissão (RLS): Execute o script supabase_setup.sql no painel do Supabase.", type: 'error' });
      } else {
        setToast({ message: "Erro na sincronização: " + err.message, type: 'error' });
      }
      setTimeout(() => setToast({ message: '', type: null }), 6000);
    } finally { setIsSyncing(false); }
  }, []);

  const triggerImmediateSync = () => setTimeout(forceSyncToSupabase, 100);

  useEffect(() => {
    const fetchFromCloud = async () => {
      if (!supabase) return;
      try {
        setIsSyncing(true);
        const { data, error } = await supabase.from('user_data').select('*').eq('id', 'multi_user_data').maybeSingle();
        
        if (error) throw error;

        if (data) {
          const sourceData = data.data || data; 
          if (sourceData.users) setUsers(sourceData.users); 
          if (sourceData.allPatients) setAllPatients(sourceData.allPatients);
          if (sourceData.allPlans) setAllPlans(sourceData.allPlans);
          if (sourceData.allAppointments) setAllAppointments(sourceData.allAppointments);
          if (sourceData.allNutritionists) setAllNutritionists(sourceData.allNutritionists);
          if (sourceData.allCashEntries) setAllCashEntries(sourceData.allCashEntries);
          if (sourceData.allReminders) setAllReminders(sourceData.allReminders);
          if (sourceData.allTasks) setAllTasks(sourceData.allTasks);
          if (sourceData.allPlanTemplates) setAllPlanTemplates(sourceData.allPlanTemplates);
          if (sourceData.allAnnualGoals) setAllAnnualGoals(sourceData.allAnnualGoals);
          if (sourceData.allMessageTemplates) setAllMessageTemplates(sourceData.allMessageTemplates);
          if (sourceData.allSettings) setAllSettings(sourceData.allSettings);
          if (sourceData.allFeedbackSettings) setAllFeedbackSettings(sourceData.allFeedbackSettings);
          if (sourceData.allFeedbackStatuses) setAllFeedbackStatuses(sourceData.allFeedbackStatuses);
          if (sourceData.allTaskCategories) setAllTaskCategories(sourceData.allTaskCategories);
          if (data.updated_at) setLastSync(new Date(data.updated_at));
        }
      } catch (err: any) { 
        console.error("Erro no download inicial:", err);
        if (err.message && (err.message.includes('row-level security') || err.code === '42501')) {
          setSyncError("Bloqueio RLS: Execute supabase_setup.sql");
        }
      } finally { setIsSyncing(false); }
    };
    fetchFromCloud();
  }, []);

  // --- MEMOIZED DATA TO PREVENT MODAL RESETS ---
  const patients = useMemo(() => currentUser ? allPatients.filter(p => p.userId === currentUser.id) : [], [allPatients, currentUser]);
  const plans = useMemo(() => currentUser ? allPlans.filter(p => p.userId === currentUser.id) : [], [allPlans, currentUser]);
  const appointments = useMemo(() => currentUser ? allAppointments.filter(a => a.userId === currentUser.id) : [], [allAppointments, currentUser]);
  const nutritionist = useMemo(() => currentUser ? (allNutritionists[currentUser.id] || null) : null, [allNutritionists, currentUser]);
  const cashEntries = useMemo(() => currentUser ? allCashEntries.filter(e => e.userId === currentUser.id) : [], [allCashEntries, currentUser]);
  const reminders = useMemo(() => currentUser ? allReminders.filter(r => r.userId === currentUser.id) : [], [allReminders, currentUser]);
  const tasks = useMemo(() => currentUser ? allTasks.filter(t => t.userId === currentUser.id) : [], [allTasks, currentUser]);
  const taskCategories = useMemo(() => currentUser ? (allTaskCategories[currentUser.id] || ["Plano alimentar", "Agendamento de consultas", "Contato com Pacientes", "Leads", "Acompanhamento de pacientes"]) : [], [allTaskCategories, currentUser]);
  const planTemplates = useMemo(() => currentUser ? allPlanTemplates.filter(t => t.userId === currentUser.id) : [], [allPlanTemplates, currentUser]);
  const annualGoals = useMemo(() => currentUser ? allAnnualGoals.filter(g => g.userId === currentUser.id) : [], [allAnnualGoals, currentUser]);
  const messageTemplates = useMemo(() => currentUser ? allMessageTemplates.filter(t => t.userId === currentUser.id || t.userId === 'all') : [], [allMessageTemplates, currentUser]);
  
  const consultationSettings = useMemo(() => currentUser ? (allSettings[currentUser.id] || {
    userId: currentUser.id, onlineEnabled: true, presentialEnabled: true, locations: [], consultationDuration: 60,
    enableProximityReminder: true, enableInstructionsReminder: true, enableReturnReminder: true, enableFeedbackReminder: true, enableBirthdayReminder: true,
    remindBeforeAppointmentDays: 1, proximityTemplateId: 1, remindWithInstructionsDays: 15, instructionsTemplateId: 999, remindReturnAfterDays: 30,
    returnTemplateId: 2, requestFeedbackAfterDays: 7, feedbackTemplateId: 1760299179687, birthdayTemplateId: 3, defaultReminderTemplateId: 1
  }) : {} as ConsultationSettings, [allSettings, currentUser]);

  const feedbackSettings = useMemo(() => currentUser ? (allFeedbackSettings[currentUser.id] || {
    userId: currentUser.id,
    nutritionistName: nutritionist?.name || '',
    sendDay: 'Segunda',
    plans: [
      { id: '1', name: 'Mensal', durationMonths: 1, standardMessage: 'Olá [NOME_PACIENTE]! Como está sendo seu primeiro mês?' },
      { id: '2', name: 'Trimestral', durationMonths: 3, standardMessage: 'Olá [NOME_PACIENTE]! Já estamos no terceiro mês!' },
      { id: '3', name: 'Semestral', durationMonths: 6, standardMessage: 'Olá [NOME_PACIENTE]! Meio ano de foco!' },
      { id: '4', name: 'Anual', durationMonths: 12, standardMessage: 'Olá [NOME_PACIENTE]! Um ano de jornada!' }
    ]
  }) : {} as FeedbackSettings, [allFeedbackSettings, currentUser, nutritionist]);

  const feedbackStatuses = useMemo(() => allFeedbackStatuses, [allFeedbackStatuses]);

  const login = async (email: string, pass: string) => {
    const user = users.find(u => u.email === email && u.password === pass);
    if (user) { setCurrentUser(user); return true; }
    return false;
  };

  const logout = () => setCurrentUser(null);
  
  const registerUser = (email: string, pass: string) => {
    setUsers(prev => [...prev, { id: Date.now().toString(), email, password: pass, role: 'nutri' as const }]);
    triggerImmediateSync();
  };

  const updateUser = async (updated: User) => {
    setUsers(prev => prev.map(u => u.id === updated.id ? updated : u));
    if (currentUser?.id === updated.id) setCurrentUser(updated);
    triggerImmediateSync();
  };
  
  const resetUserPassword = (userId: string) => { 
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, isPasswordReset: true } : u)); 
    triggerImmediateSync();
  };
  
  const setNutritionist = (n: Nutritionist | null) => { 
    if(currentUser) { 
      setAllNutritionists(prev => ({ ...prev, [currentUser.id]: n as Nutritionist })); 
      triggerImmediateSync();
    } 
  };
  
  const setPatients: React.Dispatch<React.SetStateAction<Patient[]>> = (update) => {
    if (!currentUser) return;
    setAllPatients(prev => {
      const next = typeof update === 'function' ? update(prev.filter(p => p.userId === currentUser.id)) : update;
      return [...prev.filter(p => p.userId !== currentUser.id), ...next];
    });
    triggerImmediateSync();
  };

  const setPlans: React.Dispatch<React.SetStateAction<Plan[]>> = (update) => {
    if (!currentUser) return;
    setAllPlans(prev => {
      const next = typeof update === 'function' ? update(prev.filter(p => p.userId === currentUser.id)) : update;
      return [...prev.filter(p => p.userId !== currentUser.id), ...next];
    });
    triggerImmediateSync();
  };

  const setAppointments: React.Dispatch<React.SetStateAction<Appointment[]>> = (update) => {
    if (!currentUser) return;
    setAllAppointments(prev => {
      const next = typeof update === 'function' ? update(prev.filter(a => a.userId === currentUser.id)) : update;
      return [...prev.filter(a => a.userId !== currentUser.id), ...next];
    });
    triggerImmediateSync();
  };

  const setCashEntries: React.Dispatch<React.SetStateAction<CashEntry[]>> = (update) => {
    if (!currentUser) return;
    setAllCashEntries(prev => {
      const next = typeof update === 'function' ? update(prev.filter(e => e.userId === currentUser.id)) : update;
      return [...prev.filter(e => e.userId !== currentUser.id), ...next];
    });
    triggerImmediateSync();
  };

  const setReminders: React.Dispatch<React.SetStateAction<Reminder[]>> = (update) => {
    if (!currentUser) return;
    setAllReminders(prev => {
      const next = typeof update === 'function' ? update(prev.filter(r => r.userId === currentUser.id)) : update;
      return [...prev.filter(r => r.userId !== currentUser.id), ...next];
    });
    triggerImmediateSync();
  };
  
  const addPatient = (data: any) => { 
    if (currentUser) { 
      const newId = data.id || Date.now();
      setAllPatients(prev => [...prev, { ...data, id: newId, userId: currentUser.id, createdAt: new Date().toISOString(), anthropometricAssessments: [], mealPlans: [] }]); 
      triggerImmediateSync(); 
    }
  };
  const updatePatient = (updated: Patient) => { setAllPatients(prev => prev.map(p => p.id === updated.id ? updated : p)); triggerImmediateSync(); };
  const deletePatient = (id: number) => { setAllPatients(prev => prev.filter(p => p.id !== id)); triggerImmediateSync(); };
  const addPlan = (plan: Plan) => { if (currentUser) { setAllPlans(prev => [...prev, { ...plan, userId: currentUser.id }]); triggerImmediateSync(); }};
  const updatePlan = (plan: Plan) => { setAllPlans(prev => prev.map(p => p.id === plan.id ? plan : p)); triggerImmediateSync(); };
  const deletePlan = (id: number) => { setAllPlans(prev => prev.filter(p => p.id !== id)); triggerImmediateSync(); };
  const addAppointment = (apt: Appointment) => { if (currentUser) { setAllAppointments(prev => [...prev, { ...apt, userId: currentUser.id }]); triggerImmediateSync(); }};
  const updateAppointment = (apt: Appointment) => { setAllAppointments(prev => prev.map(a => a.id === apt.id ? apt : a)); triggerImmediateSync(); };
  const deleteAppointment = (id: number) => { setAllAppointments(prev => prev.filter(a => a.id !== id)); triggerImmediateSync(); };
  const addCashEntry = (entry: CashEntry) => { if (currentUser) { setAllCashEntries(prev => [...prev, { ...entry, userId: currentUser.id }]); triggerImmediateSync(); }};
  const deleteCashEntry = (id: number) => { setAllCashEntries(prev => prev.filter(e => e.id !== id)); triggerImmediateSync(); };
  const updateCashEntryByReference = (refId: number, data: any) => { setAllCashEntries(prev => prev.map(e => e.referenceId === refId ? {...e, ...data} : e)); triggerImmediateSync(); };
  const addReminder = (r: Reminder) => { if (currentUser) { setAllReminders(prev => [...prev, { ...r, userId: currentUser.id }]); triggerImmediateSync(); }};
  const updateReminder = (r: Reminder) => { setAllReminders(prev => prev.map(item => item.id === r.id ? r : item)); triggerImmediateSync(); };
  const deleteReminder = (id: any) => { setAllReminders(prev => prev.filter(r => r.id !== id)); triggerImmediateSync(); };
  
  const addTask = (data: any) => { 
    if (currentUser) { 
      setAllTasks(prev => [...prev, { 
        ...data, 
        id: Date.now(), 
        userId: currentUser.id, 
        createdAt: new Date().toISOString() 
      }]); 
      triggerImmediateSync(); 
    }
  };
  const updateTask = (updated: Task) => { 
    setAllTasks(prev => prev.map(t => t.id === updated.id ? updated : t)); 
    triggerImmediateSync(); 
  };
  const deleteTask = (id: number) => { 
    setAllTasks(prev => prev.filter(t => t.id !== id)); 
    triggerImmediateSync(); 
  };
  const addTaskCategory = (category: string) => {
    if (currentUser) {
      setAllTaskCategories(prev => {
        const userCats = prev[currentUser.id] || ["Plano alimentar", "Agendamento de consultas", "Contato com Pacientes", "Leads", "Acompanhamento de pacientes"];
        if (userCats.includes(category)) return prev;
        return { ...prev, [currentUser.id]: [...userCats, category] };
      });
      triggerImmediateSync();
    }
  };
  const addAssessment = (pId: number, data: any) => { setAllPatients(prev => prev.map(p => p.id === pId ? { ...p, anthropometricAssessments: [...p.anthropometricAssessments, { ...data, id: Date.now() }] } : p)); triggerImmediateSync(); };
  const updateAssessment = (pId: number, upd: any) => { setAllPatients(prev => prev.map(p => p.id === pId ? { ...p, anthropometricAssessments: p.anthropometricAssessments.map(a => a.id === upd.id ? upd : a) } : p)); triggerImmediateSync(); };
  const deleteAssessment = (pId: number, aId: number) => { setAllPatients(prev => prev.map(p => p.id === pId ? { ...p, anthropometricAssessments: p.anthropometricAssessments.filter(a => a.id !== aId) } : p)); triggerImmediateSync(); };
  
  const setAnnualGoals = (update: any) => { if (currentUser) { setAllAnnualGoals(prev => {
      const next = typeof update === 'function' ? update(prev.filter(g => g.userId === currentUser.id)) : update;
      return [...prev.filter(g => g.userId !== currentUser.id), ...next];
    }); triggerImmediateSync(); }};

  const setPlanTemplates = (update: any) => { if (currentUser) { setAllPlanTemplates(prev => {
      const next = typeof update === 'function' ? update(prev.filter(t => t.userId === currentUser.id)) : update;
      return [...prev.filter(t => t.userId !== currentUser.id), ...next];
    }); triggerImmediateSync(); }};

  const setMessageTemplates = (update: any) => { if (currentUser) { setAllMessageTemplates(prev => {
      const next = typeof update === 'function' ? update(prev.filter(t => (t.userId === currentUser.id || t.userId === 'all'))) : update;
      return [...prev.filter(t => (t.userId !== currentUser.id && t.userId !== 'all')), ...next];
    }); triggerImmediateSync(); }};

  const setConsultationSettings = (val: any) => { if (currentUser) { setAllSettings(prev => ({ ...prev, [currentUser.id]: val })); triggerImmediateSync(); }};

  const setFeedbackSettings = (settings: FeedbackSettings) => {
    if (currentUser) {
      setAllFeedbackSettings(prev => ({ ...prev, [currentUser.id]: settings }));
      triggerImmediateSync();
    }
  };

  const updateFeedbackStatus = (patientId: number, date: string) => {
    setAllFeedbackStatuses(prev => {
      const filtered = prev.filter(s => s.patientId !== patientId);
      return [...filtered, { patientId, lastSentDate: date }];
    });
    triggerImmediateSync();
  };

  const syncAutoReminders = useCallback((newSettings?: ConsultationSettings) => {
    if (!currentUser) return;
    const settings = newSettings || consultationSettings;
    const now = new Date();

    setAllReminders(prev => {
      let manualAndCompleted = prev.filter(r => r.userId !== currentUser.id || !r.systemGenerated || r.isCompleted);
      const newAutoReminders: Reminder[] = [];
      
      appointments.forEach(apt => {
        const patient = patients.find(p => p.id === apt.patientId);
        if (!patient || apt.status === 'Cancelado') return;
        
        const aptDate = new Date(apt.date + 'T' + (apt.time || '12:00') + ':00');
        
        // REGRA DE OURO: Lembrete de Falta de Plano para Consultas Realizadas
        if (apt.status === 'Realizado' && !apt.linkedPlanId) {
          const reminderId = `audit-no-plan-${apt.id}`;
          if (!manualAndCompleted.some(r => r.id === reminderId)) {
            // Verifica se o paciente possui QUALQUER plano ATIVO que cubra esta data (auditoria de segurança)
            const hasCoveringPlan = plans.some(p => p.patientId === apt.patientId && p.status === 'Ativo');
            
            if (!hasCoveringPlan) {
              newAutoReminders.push({
                id: reminderId, userId: currentUser.id, patientId: apt.patientId, priority: 'Pendência Urgente',
                text: `Nenhum plano foi associado ao paciente ${patient.name} que se consultou no dia ${apt.date.split('-').reverse().join('/')} às ${apt.time} com você. Não esqueça de criar um plano para registrar o início e o final do período de acompanhamento deste paciente.`,
                date: apt.date, isCompleted: false, systemGenerated: true, autoType: 'return', sourceId: apt.id
              });
            }
          }
        }

        if (settings.enableProximityReminder) {
          const triggerDate = new Date(aptDate);
          triggerDate.setDate(triggerDate.getDate() - Number(settings.remindBeforeAppointmentDays));
          const dateStr = triggerDate.toISOString().split('T')[0];
          if (!manualAndCompleted.some(r => r.id === `auto-prox-${apt.id}`)) {
            newAutoReminders.push({
              id: `auto-prox-${apt.id}`, userId: currentUser.id, patientId: apt.patientId, priority: 'Pendência',
              text: `Enviar mensagem para o paciente ${patient.name} solicitando confirmação do agendamento.`,
              date: dateStr, isCompleted: false, systemGenerated: true, autoType: 'proximity', sourceId: apt.id
            });
          }
        }
        if (settings.enableInstructionsReminder) {
            const triggerDate = new Date(aptDate);
            triggerDate.setDate(triggerDate.getDate() - Number(settings.remindWithInstructionsDays));
            const dateStr = triggerDate.toISOString().split('T')[0];
            if (!manualAndCompleted.some(r => r.id === `auto-inst-${apt.id}`)) {
              newAutoReminders.push({
                id: `auto-inst-${apt.id}`, userId: currentUser.id, patientId: apt.patientId, priority: 'Pendência',
                text: `Enviar mensagem para o paciente ${patient.name} com informações sobre a consulta no dia ${apt.date.split('-').reverse().join('/')} às ${apt.time}.`,
                date: dateStr, isCompleted: false, systemGenerated: true, autoType: 'instructions', sourceId: apt.id
              });
            }
        }
        if (apt.status === 'Realizado') {
            if (settings.enableFeedbackReminder) {
              const triggerDate = new Date(aptDate);
              triggerDate.setDate(triggerDate.getDate() + Number(settings.requestFeedbackAfterDays));
              const dateStr = triggerDate.toISOString().split('T')[0];
              if (!manualAndCompleted.some(r => r.id === `auto-feed-${apt.id}`)) {
                newAutoReminders.push({
                  id: `auto-feed-${apt.id}`, userId: currentUser.id, patientId: apt.patientId, priority: 'Pendência',
                  text: `Enviar solicitação de feedback da consulta para o paciente ${patient.name}.`,
                  date: dateStr, isCompleted: false, systemGenerated: true, autoType: 'feedback', sourceId: apt.id
                });
              }
            }
            if (settings.enableReturnReminder) {
              const triggerDate = new Date(aptDate);
              triggerDate.setDate(triggerDate.getDate() + Number(settings.remindReturnAfterDays));
              const dateStr = triggerDate.toISOString().split('T')[0];
              if (!manualAndCompleted.some(r => r.id === `auto-ret-${apt.id}`)) {
                newAutoReminders.push({
                  id: `auto-ret-${apt.id}`, userId: currentUser.id, patientId: apt.patientId, priority: 'Pendência',
                  text: `Já fazem ${settings.remindReturnAfterDays} dias desde a última consulta do paciente ${patient.name}. Enviar mensagem para marcar retorno ou nova consulta.`,
                  date: dateStr, isCompleted: false, systemGenerated: true, autoType: 'return', sourceId: apt.id
                });
              }
            }
        }
      });
      if (settings.enableBirthdayReminder) {
        patients.forEach(p => {
          if (!p.birthDate) return;
          const birth = new Date(p.birthDate + 'T12:00:00');
          let targetDate = new Date(now.getFullYear(), birth.getMonth(), birth.getDate(), 12, 0, 0);
          if (targetDate < now && targetDate.toDateString() !== now.toDateString()) {
             targetDate.setFullYear(targetDate.getFullYear() + 1);
          }
          const dateStr = targetDate.toISOString().split('T')[0];
          const reminderId = `auto-bday-${p.id}-${targetDate.getFullYear()}`;
          if (!manualAndCompleted.some(r => r.id === reminderId)) {
            newAutoReminders.push({
              id: reminderId, userId: currentUser.id, patientId: p.id, priority: 'Pendência',
              text: `🎉 Hoje é aniversário de ${p.name}! Enviar felicitações.`,
              date: dateStr, isCompleted: false, systemGenerated: true, autoType: 'birthday', sourceId: p.id
            });
          }
        });
      }
      return [...manualAndCompleted, ...newAutoReminders];
    });
  }, [appointments, patients, plans, currentUser, consultationSettings]);

  useEffect(() => { if (currentUser) { syncAutoReminders(); } }, [appointments, patients, plans, consultationSettings, currentUser, syncAutoReminders]);

  // --- AUTOMATIC PLAN STATUS UPDATE ---
  useEffect(() => {
    if (!currentUser || allPlans.length === 0) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const expiredPlans = allPlans.filter(plan => {
      if (plan.status !== 'Ativo') return false;
      const endDate = new Date(plan.endDate + 'T23:59:59');
      return endDate < today;
    });

    if (expiredPlans.length > 0) {
      setAllPlans(prev => prev.map(plan => {
        const endDate = new Date(plan.endDate + 'T23:59:59');
        if (plan.status === 'Ativo' && endDate < today) {
          return { ...plan, status: 'Inativo' };
        }
        return plan;
      }));
      triggerImmediateSync();
    }
  }, [currentUser, allPlans, setAllPlans]);

  const importBackup = (data: any) => {
    if (!currentUser) return;
    if (data.patients) setAllPatients(prev => [...prev.filter(p => p.userId !== currentUser.id), ...data.patients.map((p: any) => ({...p, userId: currentUser.id}))]);
    if (data.plans) setAllPlans(prev => [...prev.filter(p => p.userId !== currentUser.id), ...data.plans.map((p: any) => ({...p, userId: currentUser.id}))]);
    if (data.appointments) setAllAppointments(prev => [...prev.filter(a => a.userId !== currentUser.id), ...data.appointments.map((a: any) => ({...a, userId: currentUser.id}))]);
    if (data.nutritionist) setNutritionist(data.nutritionist);
    if (data.cashEntries) setAllCashEntries(prev => [...prev.filter(e => e.userId !== currentUser.id), ...data.cashEntries.map((e: any) => ({...e, userId: currentUser.id}))]);
    if (data.reminders) setAllReminders(prev => [...prev.filter(r => r.userId !== currentUser.id), ...data.reminders.map((r: any) => ({...r, userId: currentUser.id}))]);
    if (data.planTemplates) setAllPlanTemplates(prev => [...prev.filter(t => t.userId !== currentUser.id), ...data.planTemplates.map((t: any) => ({...t, userId: currentUser.id}))]);
    if (data.annualGoals) setAllAnnualGoals(prev => [...prev.filter(g => g.userId !== currentUser.id), ...data.annualGoals.map((g: any) => ({...g, userId: currentUser.id}))]);
    if (data.messageTemplates) setAllMessageTemplates(prev => [...prev.filter(t => t.userId !== currentUser.id && t.userId !== 'all'), ...data.messageTemplates.map((t: any) => ({...t, userId: currentUser.id}))]);
    if (data.consultationSettings) setAllSettings(prev => ({ ...prev, [currentUser.id]: {...data.consultationSettings, userId: currentUser.id} }));
    triggerImmediateSync();
  };
  
  return (
    <DataContext.Provider value={{
      users, currentUser, login, logout, registerUser, updateUser, resetUserPassword,
      patients, plans, appointments, nutritionist, cashEntries, reminders, tasks, planTemplates, annualGoals, consultationSettings, messageTemplates,
      feedbackSettings, feedbackStatuses,
      setNutritionist, addPatient, updatePatient, deletePatient, setPatients, addPlan, updatePlan, deletePlan, setPlans, addAppointment, updateAppointment, deleteAppointment, setAppointments, addCashEntry, deleteCashEntry, updateCashEntryByReference, setCashEntries, addReminder, updateReminder, deleteReminder, setReminders, addTask, updateTask, deleteTask, addTaskCategory, setAnnualGoals, setPlanTemplates, setMessageTemplates, setConsultationSettings, addAssessment, updateAssessment, deleteAssessment, importBackup,
      setFeedbackSettings, updateFeedbackStatus,
      taskCategories,
      foods: foodDatabase, isSyncing, lastSync, isCloudConfigured: !!supabase, syncError, syncAutoReminders, forceSyncToSupabase,
      toast, setToast
    }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) throw new Error('useData must be used within DataProvider');
  return context;
};
